import { sha256 } from 'js-sha256'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { BlueIDAccess } from '@blueid/access-capacitor'
import { useOAuthService } from '@/services/OAuthService'
import { registerIdentityWithBackend, notifyBackendClaimed } from '@/controllers/BackboneAPI'
import { storeData, getStoredData, removeStoredItem } from '@/controllers/BaseController'
import { getCustomer } from '@/assets/Branding/branding.js'
import { showToast } from '@/services/ui/toast'
import { getCurrentUserProfile, updateUserProfileTags, deleteUserProfileTags } from '@/controllers/BaseController'
import {
  syncIdentityLocksIfMissing,
  getUserEntitledLocks,
  shouldPerformDailySync,
  recordDailySyncCompleted,
  getCachedDoors,
  setCachedDoors,
  getAllBlueIDObjects,
  getAllBlueIDDevices,
} from '@/services/blueIdGroups'
import { BlueIDSecretsPlugin, describeUnlockError } from '@/services/blueIdSecretsPlugin'

const STORE_KEYS = {
  IDENTITY: 'blueid_identity',
  CLAIMED: 'blueid_claimed_identity',
  NOTIFIED: 'blueid_claimed_identity_notified',
}

export {
  showToast,
  getUserEntitledLocks,
  syncIdentityLocksIfMissing,
  shouldPerformDailySync,
  recordDailySyncCompleted,
  getCachedDoors,
  setCachedDoors,
}

export async function getDeviceFingerprint() {
  const info = await Device.getInfo()
  const { sub = '', email = '' } = await useOAuthService().getUserProfile()
  const parts = [sub, email, info.platform, info.model, info.name, info.manufacturer, Capacitor.getPlatform()]
  const raw = parts.map(x => (x || '').toLowerCase()).join(':')
  const hash = sha256(raw)
  return parseInt(hash.slice(0, 12), 16).toString(36).padStart(7, '0').slice(0, 7)
}

// One small entry per device. Only the fields the app reads back: the whole identity
// used to land here, including the activation token (a secret that lets anyone claim
// the key until this phone does) and, from the fallback, its door list.
function updateTagObject(original, fingerprint, identity, extra = {}) {
  const merged = { ...identity, ...extra }
  const entry = {
    identityId: merged.identityId || merged._id,
    status: merged.status,
    source: merged.source,
    notified: merged.notified,
  }
  return { ...original, [fingerprint]: JSON.stringify(entry) }
}

// Revokes one identity this device registered, by its id. Never searches by email: that
// also revoked identities BlueID or facility management had made for the same person.
async function removeOwnIdentity(customer, identityId) {
  if (!identityId) return
  await BlueIDSecretsPlugin.revokeIdentity({ customer, identityId })
    .catch(err => console.warn(`[BlueID] Could not remove identity ${identityId} (safe):`, err))
}

export async function isIdentityOnDevice(identityId) {
  if (!identityId) return false
  try {
    const result = await BlueIDAccess.runCommand('getIdentities')
    const list = result?.identities || result?.value || (Array.isArray(result) ? result : [])
    const held = (Array.isArray(list) ? list : []).map(i => i?.identityId || i?.id || i?._id || i)
    console.log('[BlueID] identities on this device:', held)
    const target = String(identityId).trim().toLowerCase()
    return held.some(id => String(id).trim().toLowerCase() === target)
  } catch (err) {
    console.warn('[BlueID] Could not read on-device identities', err)
    return false
  }
}

export async function reRegisterIdentity() {
  console.warn('[BlueID] Registering identity for this install...')
  showToast('🔑 Setting up digital key…', 'info')

  await removeStoredItem(STORE_KEYS.IDENTITY)
  await removeStoredItem(STORE_KEYS.CLAIMED)
  await removeStoredItem(STORE_KEYS.NOTIFIED)
  await removeStoredItem('blueid_identity')
  await removeStoredItem('blueid_claimed_identity')
  await removeStoredItem('blueid_claimed_identity_notified')

  const fingerprint = await getDeviceFingerprint()
  const profile = await getCurrentUserProfile()
  const currentTags = { ...(profile?.tags || {}) }
  const customer = await getCustomer()

  let identity = null
  let fallbackUsed = false
  let identitySource = 'backend'

  // 1. Try Pinestack backend device-registration first (only for Hammerbrooklyn)
  if (customer !== 'pia') {
    try {
      console.log('[BlueID] Attempting backend v2/device-registration first...')
      const backendPromise = registerIdentityWithBackend(fingerprint, true)
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Backend timeout')), 3500))
      const result = await Promise.race([backendPromise, timeoutPromise])
      if (result?.identityId && result?.activationToken) {
        identity = result
        identitySource = 'backend'
        console.log('[BlueID] Successfully obtained identity from backend:', identity.identityId)
      }
    } catch (err) {
      fallbackUsed = true
      console.warn('[BlueID] Backend device-registration failed or timed out, falling back to direct BlueID Secrets plugin:', err)
    }
  }

  // 2. Direct BlueID Secrets fallback if backend fails or is missing token
  if (!identity || !identity.identityId || !identity.activationToken) {
    fallbackUsed = true
    identitySource = 'secrets'
    try {
      console.log('[BlueID] Backup plan: establishing identity via BlueIDSecretsPlugin...')
      const email = profile?.email || profile?.upn || profile?.preferred_username || ''
      const name = profile?.name || profile?.given_name || email || fingerprint
      // Roles live in the login token; the Pinestack profile above carries none.
      const tokenProfile = await useOAuthService().getUserProfile().catch(() => null)
      const roles = tokenProfile?.realm_access?.roles || profile?.realm_access?.roles || profile?.roles || []
      const isMasterUser = roles.some(r => /globaladmin|superadmin|supermember|master/i.test(r))
      const { model = '' } = await Device.getInfo().catch(() => ({}))

      // Always a new identity for this device: an existing usable one was already
      // reused in hydrateIdentity, and searching the site's identities by email also
      // caught (and revoked) identities other people made for the same person.
      const now = new Date()
      const validTo = new Date(now)
      validTo.setFullYear(validTo.getFullYear() + 1)

      const payload = {
        // The device name tells several keys of one person apart in the BlueID portal.
        name: (model ? `${name} · ${model}` : name).slice(0, 60),
        description: fingerprint,
        email: email || 'mobile@pinestack.com',
        mobilePhone: '+490000000000',
        role: isMasterUser ? 'master' : 'regular',
        validFrom: now.toISOString(),
        validTo: validTo.toISOString(),
        refresh: 30,
        ephemeral: false,
        schedules: [],
        externalGroupId: null,
        groups: [],
        objects: [],
      }

      const existingIdentity = await BlueIDSecretsPlugin.createIdentity({ customer, payload })

      if (existingIdentity?._id) {
        let token = existingIdentity.activationToken
        if (!token) {
          const res = await BlueIDSecretsPlugin.renewActivationToken({
            customer,
            identityId: existingIdentity._id,
          })
          token = res?.activationToken
        }

        identity = {
          identityId: existingIdentity._id,
          activationToken: token || '',
          source: 'secrets',
          ...existingIdentity,
        }
      }
    } catch (pluginErr) {
      console.error('[BlueID] BlueIDSecrets fallback also failed:', pluginErr)
      throw new Error('Could not establish digital key for this device. Please try again.', { cause: pluginErr })
    }
  }

  if (!identity?.identityId || !identity?.activationToken) {
    throw new Error('No activation token received for lock setup.')
  }

  identity.source = identitySource
  await storeData(STORE_KEYS.IDENTITY, identity)
  await storeData(`blueid_identity_${customer}`, identity)

  const updatedTags = updateTagObject(currentTags, fingerprint, identity, {
    status: 'REGISTERED',
    source: identitySource,
    notified: false,
  })
  updateUserProfileTags(profile?.id, updatedTags).catch(e => console.warn('Tag update warning:', e))
  console.log('✅ Identity registered and stored:', identity.identityId, `(source: ${identitySource})`)
  return { source: 'new', identity, claimed: false, notified: false, fallbackUsed }
}

export async function hydrateIdentity({ forceFresh = false } = {}) {
  if (forceFresh) {
    return await reRegisterIdentity()
  }

  const fingerprint = await getDeviceFingerprint()
  const profile = await getCurrentUserProfile()
  const tags = { ...(profile?.tags || {}) }
  const customer = await getCustomer()

  // Each device keeps its own identity: another phone or tablet of the same person is
  // never touched from here. Logout on that device removes its identity.

  // 1. Check local storage for current device (environment-specific)
  const local = (await getStoredData(`blueid_identity_${customer}`)) ||
    (customer !== 'pia' ? await getStoredData(STORE_KEYS.IDENTITY) : null)
  // A claimed key no longer needs its activation token (and tags no longer carry one).
  if (local?.identityId) {
    const isDeviceHeld = await isIdentityOnDevice(local.identityId)
    const isLocalClaimed = Boolean(
      (await getStoredData(`blueid_claimed_${customer}`)) ||
      (await getStoredData(STORE_KEYS.CLAIMED))
    )

    if (isDeviceHeld) {
      if (!isLocalClaimed) {
        await storeData(STORE_KEYS.CLAIMED, true)
        await storeData(`blueid_claimed_${customer}`, true)
      }
      const notified = !!(
        (await getStoredData(`blueid_notified_${customer}`)) ||
        (await getStoredData(STORE_KEYS.NOTIFIED))
      )?.['_id']
      if (!notified && customer !== 'pia') {
        confirmBackendClaim().catch(e => console.warn(e))
      }
      // Synchronize only if forceFresh was explicitly requested (e.g. manual resync)
      if (forceFresh) {
        await synchronizeIdentity(local.identityId, true)
      }
      return {
        source: 'store',
        identity: local,
        claimed: true,
        notified: true,
      }
    }

    // Local identity is not held on device: clear stale local keys
    await removeStoredItem(STORE_KEYS.IDENTITY)
    await removeStoredItem(`blueid_identity_${customer}`)
    await removeStoredItem(STORE_KEYS.CLAIMED)
    await removeStoredItem(STORE_KEYS.NOTIFIED)
  }

  // 2. Check profile tags for current device
  if (tags[fingerprint]) {
    try {
      let parsed = tags[fingerprint]
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed)
      }

      if (parsed && parsed.identityId) {
        const isDeviceHeld = await isIdentityOnDevice(parsed.identityId)

        if (isDeviceHeld) {
          await storeData(STORE_KEYS.IDENTITY, parsed)
          await storeData(STORE_KEYS.CLAIMED, true)
          if (!parsed.notified) {
            confirmBackendClaim().catch(e => console.warn(e))
          } else {
            await storeData(STORE_KEYS.NOTIFIED, { _id: parsed.identityId })
          }
          // Synchronize only if forceFresh was explicitly requested
          if (forceFresh) {
            await synchronizeIdentity(parsed.identityId, true)
          }
          return {
            source: 'tags',
            identity: parsed,
            claimed: true,
            notified: true,
          }
        }

        // Same physical device, but app cache was cleared or reinstalled:
        // try renewing the activation token first to keep the existing identity.
        try {
          console.log(`[BlueID] Device ${fingerprint} lacks local keys. Attempting to renew activation token for identity ${parsed.identityId}...`)
          const { activationToken } = await BlueIDSecretsPlugin.renewActivationToken({
            customer,
            identityId: parsed.identityId,
          })
          if (activationToken) {
            parsed.activationToken = activationToken
            await storeData(STORE_KEYS.IDENTITY, parsed)
            await claimIdentityWithSDK(activationToken)
            await confirmBackendClaim().catch(e => console.warn(e))
            await synchronizeIdentity(parsed.identityId, true)
            await storeData(STORE_KEYS.CLAIMED, true)
            return {
              source: 'renewed',
              identity: parsed,
              claimed: true,
              notified: true,
            }
          }
        } catch (renewErr) {
          console.warn('[BlueID] Existing identity token renewal failed (identity already activated or expired on cloud):', renewErr)
        }

        // Remove the orphaned identity so it does not linger, then create a clean one.
        console.log(`[BlueID] Removing orphaned identity ${parsed.identityId} and creating a fresh digital key...`)
        await removeOwnIdentity(customer, parsed.identityId)
      }
    } catch (e) {
      console.warn('⚠️ Failed to parse tag identity:', tags[fingerprint], e)
    }
  }

  return await reRegisterIdentity()
}

export async function claimIdentityWithSDK(activationToken) {
  if (!activationToken) return
  try {
    await BlueIDAccess.runCommand('claimIdentity', activationToken.trim())
    const [profile, fingerprint, identity] = await Promise.all([
      getCurrentUserProfile(),
      getDeviceFingerprint(),
      getStoredData(STORE_KEYS.IDENTITY),
    ])

    await storeData(STORE_KEYS.CLAIMED, true)
    showToast('🔐 Digital key activated.', 'success')
    const updatedTags = updateTagObject(profile?.tags || {}, fingerprint, identity, { status: 'CLAIMED' })
    updateUserProfileTags(profile?.id, updatedTags).catch(e => console.warn('Tag update background warning:', e))
    console.log('🔐 Identity claimed and tag updated')
  } catch (err) {
    console.warn('⚠️ SDK claim warning:', err)
    if (/already claimed|already exists/i.test(err?.message || '')) {
      // Identity was already claimed in local SDK
      await storeData(STORE_KEYS.CLAIMED, true)
      const [profile, fingerprint, identity] = await Promise.all([
        getCurrentUserProfile(),
        getDeviceFingerprint(),
        getStoredData(STORE_KEYS.IDENTITY),
      ])
      const updatedTags = updateTagObject(profile?.tags || {}, fingerprint, identity, { status: 'CLAIMED' })
      updateUserProfileTags(profile?.id, updatedTags).catch(() => {})
      return
    }
    try {
      const customer = await getCustomer()
      const identity = (await getStoredData(`blueid_identity_${customer}`)) || (await getStoredData(STORE_KEYS.IDENTITY))
      if (identity?.identityId) {
        const { activationToken: freshToken } = await BlueIDSecretsPlugin.renewActivationToken({
          customer,
          identityId: identity.identityId,
        })
        if (freshToken) {
          await BlueIDAccess.runCommand('claimIdentity', freshToken.trim())
          await storeData(STORE_KEYS.CLAIMED, true)
          showToast('🔐 Digital key activated.', 'success')
          const [profile, fingerprint] = await Promise.all([
            getCurrentUserProfile(),
            getDeviceFingerprint(),
          ])
          const updatedTags = updateTagObject(profile?.tags || {}, fingerprint, identity, { status: 'CLAIMED' })
          updateUserProfileTags(profile?.id, updatedTags).catch(() => {})
          return
        }
      }
    } catch (retryErr) {
      console.warn('SDK claim retry warning:', retryErr)
    }
  }
}

export async function confirmBackendClaim() {
  const identity = await getStoredData(STORE_KEYS.IDENTITY)
  const notified = await getStoredData(STORE_KEYS.NOTIFIED)
  if (!identity?.identityId) return { skipped: true }

  // If using secrets plugin or no identity, NO need to notify backend
  if (identity.source === 'secrets') {
    console.log('[BlueID] Skipping backend claim notification (identity was registered via secrets plugin fallback).')
    return { skipped: true }
  }

  if (notified?._id === identity.identityId) return { alreadyNotified: true }

  let notifySuccess
  try {
    console.log('[BlueID] Notifying backend that identity was claimed:', identity.identityId)
    await notifyBackendClaimed(identity.identityId)
    await storeData(STORE_KEYS.NOTIFIED, { _id: identity.identityId })
    notifySuccess = true
  } catch (err) {
    console.warn('[BlueID] Backend claim notification warning (safe, continuing without notifying):', err)
    return { success: false, warning: true, error: err }
  }

  const [profile, fingerprint] = await Promise.all([getCurrentUserProfile(), getDeviceFingerprint()])
  const updatedTags = updateTagObject(profile?.tags || {}, fingerprint, identity, {
    status: 'CLAIMED',
    notified: notifySuccess,
    source: identity.source || 'backend',
  })
  updateUserProfileTags(profile?.id, updatedTags).catch(e => console.warn('Tag update warning:', e))
  return { success: notifySuccess }
}

export async function addIdentityWithSDK() {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    if (!identity?.identityId) return { skipped: true }

    const { identities = [] } = (await BlueIDAccess.runCommand('getIdentities').catch(() => ({}))) || {}
    const target = String(identity.identityId).trim().toLowerCase()
    if (identities.some(i => String(i?.identityId || i?.id || i?._id).trim().toLowerCase() === target)) {
      return { alreadyExists: true }
    }

    return { alreadyExists: false }
  } catch (err) {
    console.warn('[BlueID] addIdentity check:', err)
    return { alreadyExists: true }
  }
}

export async function synchronizeIdentity(customIdentityId, forceRefresh = true) {
  try {
    const identity = await getStoredData(STORE_KEYS.IDENTITY)
    const targetId = customIdentityId || identity?.identityId
    if (!targetId) {
      console.log('[BlueID] No specific identity ID found. Calling SDK synchronizeIdentities fallback...')
      const result = await BlueIDAccess.runCommand('synchronizeIdentities', forceRefresh)
      return { success: true, result }
    }
    const result = await BlueIDAccess.runCommand('synchronizeIdentity', targetId, forceRefresh)
    console.log('[BlueID] Identity synchronized with forceRefresh =', forceRefresh)
    return { success: true, result }
  } catch (err) {
    console.error('[BlueID] Failed to synchronize identity:', err)
    const errMsg = String(err?.message || err || '')
    if (err?.code === -427 || err?.code === -428 || /revoked|deleted/i.test(errMsg)) {
      console.warn('[BlueID] Identity revoked/deleted during sync. Triggering state reset...')
      await resetBlueIDState({ clearSdkData: true }).catch(() => {})
    }
    return { success: false, error: err }
  }
}

export async function synchronizeAllIdentities(forceRefresh = true) {
  try {
    const result = await BlueIDAccess.runCommand('synchronizeIdentities', forceRefresh)
    console.log('[BlueID] All on-device identities synchronized with forceRefresh =', forceRefresh)
    return { success: true, result }
  } catch (err) {
    console.error('[BlueID] Failed to synchronize all identities:', err)
    return { success: false, error: err }
  }
}

export async function getAccessObjects() {
  try {
    const customer = await getCustomer().catch(() => '')
    const identity = (await getStoredData(`blueid_identity_${customer}`)) || (await getStoredData(STORE_KEYS.IDENTITY))
    if (!identity?.identityId) return []

    // Concurrently fetch access objects from SDK and live BlueID Cloud catalogs
    const [res, devListRes, cloudLocksRes, cloudDevicesRes] = await Promise.all([
      BlueIDAccess.runCommand('getAccessObjects', identity.identityId).catch(err => {
        console.warn('[BlueID] getAccessObjects command warning:', err)
        return null
      }),
      BlueIDAccess.runCommand('listAccessDevices', identity.identityId).catch(err => {
        console.warn('[BlueID] listAccessDevices command warning:', err)
        return null
      }),
      getAllBlueIDObjects(customer).then(objects => ({ objects })).catch(err => {
        console.warn('[BlueID] Cloud getLocks warning:', err)
        return null
      }),
      getAllBlueIDDevices(customer).then(devices => ({ devices })).catch(err => {
        console.warn('[BlueID] Cloud getDevices warning:', err)
        return null
      }),
    ])

    const objects = Array.isArray(res)
      ? res
      : res?.objects || res?.accessObjects || res?.value || []

    const accessDevices = Array.isArray(devListRes)
      ? devListRes
      : devListRes?.devices || []

    // Build lookup maps from SDK listAccessDevices
    const deviceMapByObjectId = new Map()
    const deviceMapByName = new Map()
    accessDevices.forEach(d => {
      const devId = d?.deviceId || d?.id
      if (!devId) return
      if (d.objectId) {
        if (!deviceMapByObjectId.has(d.objectId)) deviceMapByObjectId.set(d.objectId, [])
        deviceMapByObjectId.get(d.objectId).push(devId)
      }
      if (d.objectName) {
        const normName = d.objectName.trim().toLowerCase()
        if (!deviceMapByName.has(normName)) deviceMapByName.set(normName, [])
        deviceMapByName.get(normName).push(devId)
      }
    })

    // Index cloud objects and cloud devices from BlueID Cloud
    const cloudObjects = Array.isArray(cloudLocksRes?.objects) ? cloudLocksRes.objects : []
    const cloudDevices = Array.isArray(cloudDevicesRes?.devices) ? cloudDevicesRes.devices : []

    const cloudObjByObjectId = new Map()
    const cloudObjById = new Map()
    const cloudObjByName = new Map()
    cloudObjects.forEach(co => {
      if (co.objectId !== undefined) cloudObjByObjectId.set(Number(co.objectId), co)
      if (co._id) cloudObjById.set(String(co._id), co)
      if (co.name) cloudObjByName.set(co.name.trim().toLowerCase(), co)
    })

    const cloudDevByObjectId = new Map()
    const cloudDevByName = new Map()
    cloudDevices.forEach(cd => {
      const devId = cd.deviceId || cd.id
      if (!devId) return
      if (cd.object) {
        const key = String(cd.object)
        if (!cloudDevByObjectId.has(key)) cloudDevByObjectId.set(key, [])
        cloudDevByObjectId.get(key).push(devId)
      }
      if (cd.objectName) {
        const key = cd.objectName.trim().toLowerCase()
        if (!cloudDevByName.has(key)) cloudDevByName.set(key, [])
        cloudDevByName.get(key).push(devId)
      }
    })

    const normalized = objects
      .map(o => {
        const cloudObj =
          cloudObjByObjectId.get(Number(o.objectId)) ||
          cloudObjById.get(String(o.Id || o._id || o.objectId)) ||
          cloudObjByName.get(String(o.name || '').trim().toLowerCase())

        const devIds = []

        // 1. From SDK object devices / deviceIds
        const sdkDevs = o.devices || o.deviceIds || []
        sdkDevs.forEach(d => {
          if (typeof d === 'string') {
            devIds.push(d)
          } else if (d && typeof d === 'object') {
            if (d.deviceId) devIds.push(d.deviceId)
            if (d.blueDeviceId) devIds.push(d.blueDeviceId)
            if (d.hardwareId) devIds.push(d.hardwareId)
            if (d.id) devIds.push(d.id)
            if (d._id) devIds.push(d._id)
          }
        })

        // 2. From SDK listAccessDevices
        if (o.objectId && deviceMapByObjectId.has(o.objectId)) {
          devIds.push(...deviceMapByObjectId.get(o.objectId))
        }
        if (o.name && deviceMapByName.has(o.name.trim().toLowerCase())) {
          devIds.push(...deviceMapByName.get(o.name.trim().toLowerCase()))
        }

        // 3. From Cloud object devices & legacySecureObjectId
        if (cloudObj) {
          if (Array.isArray(cloudObj.devices)) {
            cloudObj.devices.forEach(d => {
              if (typeof d === 'string') {
                devIds.push(d)
              } else if (d && typeof d === 'object') {
                if (d.deviceId) devIds.push(d.deviceId)
                if (d.blueDeviceId) devIds.push(d.blueDeviceId)
                if (d.hardwareId) devIds.push(d.hardwareId)
                if (d.id) devIds.push(d.id)
                if (d._id) devIds.push(d._id)
              }
            })
          }
          if (cloudObj.legacySecureObjectId) {
            devIds.push(cloudObj.legacySecureObjectId)
          }
          if (cloudObj._id && cloudDevByObjectId.has(String(cloudObj._id))) {
            devIds.push(...cloudDevByObjectId.get(String(cloudObj._id)))
          }
        }

        // 4. From Cloud devices matching object name
        if (o.name && cloudDevByName.has(o.name.trim().toLowerCase())) {
          devIds.push(...cloudDevByName.get(o.name.trim().toLowerCase()))
        }

        // 5. If object's own name is a hardware device ID (8-16 alphanumeric characters)
        if (o.name && /^[A-Za-z0-9_-]{8,16}$/.test(o.name.trim())) {
          devIds.push(o.name.trim())
        }

        // 6. Direct legacySecureObjectId on object if present
        if (o.legacySecureObjectId) {
          devIds.push(o.legacySecureObjectId)
        }

        const validHardwareIds = [...new Set(devIds.map(x => String(x).trim()))]
          .filter(x => x.length >= 6 && !/^\d+$/.test(x) && !/^[0-9a-f]{24}$/i.test(x))

        const legacyId = cloudObj?.legacySecureObjectId || o.legacySecureObjectId || null

        return {
          objectId: o.Id || o._id || o.id || o.objectId,
          name: o.name || 'Door',
          description: o.description || '',
          legacySecureObjectId: legacyId,
          deviceIds: validHardwareIds.length
            ? validHardwareIds
            : (legacyId ? [legacyId] : [String(o.objectId || o.Id || o._id)]),
          groups: o.groups || [],
          raw: { ...cloudObj, ...o },
        }
      })

    // For GlobalAdmin / Master users, ensure all Cloud objects are present so admin sees all doors
    const profile = await useOAuthService().getUserProfile().catch(() => ({}))
    const roles = profile?.realm_access?.roles || profile?.roles || []
    const isGlobalAdmin = roles.some(r => /globaladmin|superadmin|supermember|master/i.test(r))

    if (isGlobalAdmin && cloudObjects.length > 0) {
      cloudObjects.forEach(co => {
        const alreadyInList = normalized.some(
          d =>
            d.objectId === co.objectId ||
            d.objectId === co._id ||
            d.name?.trim().toLowerCase() === co.name?.trim().toLowerCase()
        )
        if (!alreadyInList) {
          const legacyId = co.legacySecureObjectId || null
          const devIds = []
          if (Array.isArray(co.devices)) {
            co.devices.forEach(d => {
              if (typeof d === 'string') {
                devIds.push(d)
              } else if (d && typeof d === 'object') {
                if (d.deviceId) devIds.push(d.deviceId)
                if (d.blueDeviceId) devIds.push(d.blueDeviceId)
                if (d.hardwareId) devIds.push(d.hardwareId)
                if (d.id) devIds.push(d.id)
                if (d._id) devIds.push(d._id)
              }
            })
          }
          if (legacyId) devIds.push(legacyId)
          if (co._id && cloudDevByObjectId.has(String(co._id))) {
            devIds.push(...cloudDevByObjectId.get(String(co._id)))
          }
          if (co.name && /^[A-Za-z0-9_-]{8,16}$/.test(co.name.trim())) {
            devIds.push(co.name.trim())
          }
          const validHardwareIds = [...new Set(devIds.map(x => String(x).trim()))]
            .filter(x => x.length >= 6 && !/^\d+$/.test(x) && !/^[0-9a-f]{24}$/i.test(x))

          normalized.push({
            objectId: co._id || co.objectId,
            name: co.name || 'Door',
            description: co.description || '',
            legacySecureObjectId: legacyId,
            deviceIds: validHardwareIds.length
              ? validHardwareIds
              : (legacyId ? [legacyId] : [String(co.objectId)]),
            groups: co.groups || [],
            raw: co,
          })
        }
      })
    }

    // Staging test locks (only active for staging/pia builds, never in hammerbrooklyn)
    if (customer === 'pia') {
      const PRE_KNOWLEDGE_LOCKS = [
        {
          objectId: '67efb790531e22f79c6ccdcb',
          name: 'Pinestack Door',
          description: 'Frontdoor Pinestack · Test Lock',
          deviceIds: ['1j67Ugtm', 'Dt5LHVPzPg'],
          legacySecureObjectId: 'Dt5LHVPzPg',
          devices: [{ id: '684298bd9885990bf73301e2', deviceId: '1j67Ugtm' }],
          groups: [],
          raw: { objectId: 2, name: 'Pinestack Door', legacySecureObjectId: 'Dt5LHVPzPg' },
        },
        {
          objectId: '67f798363a0e77d51cf36f3f',
          name: 'DELETE 1',
          description: 'Cylinder Lock (Test)',
          deviceIds: ['NALLhQId'],
          legacySecureObjectId: null,
          devices: [{ id: '67f798363a0e77d51cf36f3f', deviceId: 'NALLhQId' }],
          groups: [],
          raw: { objectId: 5, name: 'DELETE 1' },
        },
        {
          objectId: '67fd09d353ad24e62f49766a',
          name: 'DELETE 2',
          description: 'Wall Reader (Test)',
          deviceIds: ['orde4V7c'],
          legacySecureObjectId: null,
          devices: [{ id: '67fd09d353ad24e62f49766a', deviceId: 'orde4V7c' }],
          groups: [],
          raw: { objectId: 6, name: 'DELETE 2' },
        },
      ]

      PRE_KNOWLEDGE_LOCKS.forEach(testLock => {
        const existing = normalized.find(
          d => d.name?.trim().toLowerCase() === testLock.name.trim().toLowerCase()
        )
        if (existing) {
          existing.deviceIds = [...new Set([...(existing.deviceIds || []), ...testLock.deviceIds])].filter(
            x => x.length >= 6 && !/^\d+$/.test(x) && !/^[0-9a-f]{24}$/i.test(x)
          )
          if (testLock.legacySecureObjectId && !existing.legacySecureObjectId) {
            existing.legacySecureObjectId = testLock.legacySecureObjectId
          }
        } else {
          normalized.push(testLock)
        }
      })
    }

    console.log('[BlueID] Final enriched access objects list:', normalized.length, normalized)
    return normalized
  } catch (err) {
    console.error('[BlueID] Failed to fetch access objects:', err)
    return []
  }
}

let unlockInFlight = false

export async function tryUnlockBlueIDDevice(deviceId) {
  if (unlockInFlight) {
    return { success: false, error: 'Another door operation is already in progress. Please wait a moment.' }
  }

  unlockInFlight = true
  try {
    const customer = await getCustomer()
    const identity = (await getStoredData(`blueid_identity_${customer}`)) || (await getStoredData(STORE_KEYS.IDENTITY))
    if (!identity?.identityId) {
      throw new Error('No active digital key found on this device.')
    }
    const result = await BlueIDAccess.runCommand('tryAccessDevice', identity.identityId, deviceId)

    // Inspect BlueOssAccessResult returned by SDK
    if (result && typeof result === 'object') {
      if (result.accessGranted === false) {
        if (result.scheduleMissmatch) {
          return { success: false, error: 'Access denied: credentials are not valid at this time or day.' }
        }
        return { success: false, error: 'Access denied by the lock for this digital key.' }
      }
      if (result.returnCode !== undefined && result.returnCode !== 0) {
        const errorMsg = describeUnlockError({ code: result.returnCode, message: result.errorDescription })
        return { success: false, error: errorMsg }
      }
    }

    return { success: true, result }
  } catch (err) {
    const errMsg = String(err?.message || err?.errorMessage || err || '')
    const errCode = err?.code

    // Edge case: Identity revoked or deleted on server (-427, -428)
    if (errCode === -427 || errCode === -428 || /revoked|deleted/i.test(errMsg)) {
      console.warn('[BlueID] Identity revoked or deleted during unlock. Resetting stored state...')
      await resetBlueIDState({ clearSdkData: true }).catch(() => {})
      const errorMsg = describeUnlockError(err)
      return { success: false, error: errorMsg }
    }

    // A failed unlock never syncs on its own: most failures are Bluetooth or distance.
    // Re-sync in the app refreshes the key when it really is out of date.
    if (/no OsSO|OSSid|no token|ticket|-404/i.test(errMsg) || errCode === -404) {
      return { success: false, error: 'Your digital key has no access to this door yet. Tap Re-sync to update it.' }
    }
    const errorMsg = describeUnlockError(err)
    return { success: false, error: errorMsg }
  } finally {
    unlockInFlight = false
  }
}

export async function getIdentitiesFromSDK() {
  try {
    const result = await BlueIDAccess.runCommand('getIdentities')
    return result?.identities || []
  } catch (err) {
    return []
  }
}

export async function revokeActiveIdentityIfExists() {
  try {
    const customer = await getCustomer()
    const fingerprint = await getDeviceFingerprint().catch(() => null)
    const local = (await getStoredData(STORE_KEYS.IDENTITY)) || (await getStoredData(`blueid_identity_${customer}`))
    let identityId = local?.identityId
    let profile = null

    try {
      profile = await getCurrentUserProfile()
    } catch (profileErr) {
      console.warn('⚠️ Could not fetch current user profile during logout:', profileErr)
    }

    const tags = profile?.tags || {}
    // Only this device's own identity: from local storage or this device's profile tag.
    // Never search the BlueID site by email — that revoked identities other people
    // (BlueID, facility management) had created for the same person.
    const identitiesToRevoke = new Set()
    if (identityId) identitiesToRevoke.add(identityId)

    if (fingerprint && tags[fingerprint]) {
      try {
        const parsed = typeof tags[fingerprint] === 'string' ? JSON.parse(tags[fingerprint]) : tags[fingerprint]
        const tagId = parsed?.identityId || parsed?._id || parsed?.id
        if (tagId) identitiesToRevoke.add(tagId)
      } catch (e) {
        console.warn('⚠️ Tag parse error:', e)
      }
    }

    for (const id of identitiesToRevoke) {
      console.log(`[BlueID] Removing this device's identity ${id} during logout/cleanup...`)
      await removeOwnIdentity(customer, id)
    }

    // 3. Remove this device's tag. Other devices' tags stay: their identities still work.
    // Always by key: the profile above can come from the app's cache and miss a tag
    // written during this session.
    if (profile?.id) {
      const uniqueKeys = fingerprint ? [fingerprint] : []
      if (uniqueKeys.length > 0) {
        console.log(`[BlueID] Deleting tags [${uniqueKeys.join(', ')}] from user profile ${profile.id}...`)
        try {
          await deleteUserProfileTags(profile.id, uniqueKeys)
          console.log(`✅ Device tags successfully deleted from backend profile:`, uniqueKeys)
        } catch (delErr) {
          console.warn('⚠️ Failed to DELETE tags directly, trying updateUserProfileTags fallback:', delErr)
          try {
            const updated = { ...tags }
            for (const k of uniqueKeys) delete updated[k]
            await updateUserProfileTags(profile.id, updated)
            console.log(`✅ Device tags pruned via profile update fallback:`, uniqueKeys)
          } catch (fallbackErr) {
            console.warn('⚠️ Tag pruning fallback warning:', fallbackErr)
          }
        }
      }
    }

    // 4. Wipe local SDK cryptographic data and preferences
    await resetBlueIDState({ clearSdkData: true })
  } catch (e) {
    console.warn('⚠️ Revoke active identity warning:', e)
  }
}

export async function resetBlueIDState({ clearSdkData = false } = {}) {
  const keysToRemove = [
    STORE_KEYS.IDENTITY,
    STORE_KEYS.CLAIMED,
    STORE_KEYS.NOTIFIED,
    'blueid_identity',
    'blueid_claimed_identity',
    'blueid_claimed_identity_notified',
    'blueid_identity_hammerbrooklyn',
    'blueid_claimed_hammerbrooklyn',
    'blueid_notified_hammerbrooklyn',
    'blueid_identity_pia',
    'blueid_claimed_pia',
    'blueid_notified_pia',
    'blueid_cached_doors',
    'blueid_last_daily_sync_time',
  ]

  for (const k of keysToRemove) {
    await removeStoredItem(k).catch(() => {})
  }

  if (Capacitor.isNativePlatform()) {
    try {
      const { Preferences } = await import('@capacitor/preferences')
      for (const k of keysToRemove) {
        await Preferences.remove({ key: k }).catch(() => {})
      }
    } catch (e) {
      void e
    }
  }

  if (clearSdkData && Capacitor.isNativePlatform()) {
    try {
      // BlueID SDK command for clearing all local credentials and data is UNSAFE_clearData
      await BlueIDAccess.runCommand('UNSAFE_clearData').catch(err => {
        console.warn('⚠️ UNSAFE_clearData fallback:', err)
      })
    } catch (e) {
      console.warn('⚠️ SDK data clear warning:', e)
    }
  }

  try {
    const { stopBleScanning } = await import('@/services/BleScannerManager')
    await stopBleScanning().catch(() => {})
  } catch (e) {
    void e
  }
}
